<template>
  <div class="szw-network">

    <!-- Hero -->
    <section class="szw-network-hero">
      <div class="szw-network-hero__bg">
        <svg class="szw-network-hero__bg-shape szw-network-hero__bg-shape--right" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#ffffff" opacity="0.05"/>
        </svg>
        <div class="szw-network-hero__blob szw-network-hero__blob--teal"></div>
        <div class="szw-network-hero__blob szw-network-hero__blob--gold"></div>
      </div>

      <div class="szw-container szw-network-hero__content">
        <div class="tw-flex items-center gap-3">
          <div class="szw-network-hero__icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 28px; color: #071c5b;">hub</span>
          </div>
          <div>
            <h1 class="szw-network-hero__title">{{ $t('samergo_my_network_title') }}</h1>
            <p class="szw-network-hero__subtitle">{{ $t('samergo_home_action_network_subtitle') }}</p>
            <div class="tw-flex items-center gap-1.5 mt-1">
              <span class="material-symbols-outlined" style="font-size: 14px; color: rgba(219,234,254,0.7);">location_on</span>
              <span style="font-size: 0.82rem; color: rgba(219,234,254,0.7);">{{ profile.location_label || $t('samergo_regio_no_location_title') }}</span>
              <button
                class="szw-network-hero__edit-location-btn"
                @click="openProfileModal"
              >
                <span class="material-symbols-outlined" style="font-size: 13px;">edit</span>
              </button>
            </div>
          </div>
          <span v-if="contacts.length" class="szw-network-hero__count">
            {{ contacts.length === 1 ? $t('samergo_my_network_contact_one', { count: 1 }) : $t('samergo_my_network_contacts_many', { count: contacts.length }) }}
          </span>
        </div>

        <!-- Filter tabs -->
        <div v-if="contacts.length" class="szw-network-hero__tabs">
          <button
            class="szw-network-hero__tab"
            :class="{ 'szw-network-hero__tab--active': activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >{{ $t('samergo_my_network_filter_all') }}
          </button>
          <button
            v-for="claim in myClaims"
            :key="claim.actor_id"
            class="szw-network-hero__tab tw-flex items-center gap-1.5"
            :class="{ 'szw-network-hero__tab--active': activeFilter === claim.actor_id }"
            @click="activeFilter = claim.actor_id"
          >
            <img v-if="claim.logo" :src="claim.logo" class="w-4 h-4 rounded object-cover flex-shrink-0"/>
            {{ claim.name }}
          </button>
        </div>

        <!-- Search -->
        <div v-if="contacts.length" class="szw-network-hero__search-wrapper">
          <div class="szw-network-hero__search-glow"></div>
          <div class="szw-network-hero__search-box">
            <span class="material-symbols-outlined szw-network-hero__search-icon">search</span>
            <input
              v-model="searchQuery"
              class="szw-network-hero__search-input"
              :placeholder="$t('samergo_my_network_search_placeholder')"
              type="text"
            />
            <button v-if="searchQuery" class="szw-network-hero__search-clear" @click="searchQuery = ''">
              <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Content -->
    <section class="szw-section">
      <div class="szw-container">

        <!-- Empty state -->
        <div v-if="!contacts.length" class="szw-network__empty">
          <div class="szw-network__empty-icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">group</span>
          </div>
          <h2 class="szw-network__empty-title">{{ $t('samergo_my_network_empty_title') }}</h2>
          <p class="szw-network__empty-body">{{ $t('samergo_my_network_empty_body') }}</p>
          <button class="szw-network__btn szw-network__btn--primary" @click="$router.push('/actors-simplified')">
            <span class="material-symbols-outlined" style="font-size: 18px;">person_search</span>
            {{ $t('samergo_my_network_discover_cta') }}
          </button>
        </div>

        <!-- No filtered results -->
        <div v-else-if="!filteredContacts.length" class="szw-network__empty">
          <div class="szw-network__empty-icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">search_off</span>
          </div>
          <p class="szw-network__empty-body">
            <template v-if="searchQuery">{{ $t('samergo_my_network_no_results_search', { query: searchQuery }) }}</template>
            <template v-else>{{ $t('samergo_my_network_no_results_filter') }}</template>
          </p>
          <button v-if="searchQuery" class="szw-network__btn szw-network__btn--ghost" @click="searchQuery = ''">
            {{ $t('samergo_my_network_clear_search') }}
          </button>
          <button v-else class="szw-network__btn szw-network__btn--ghost" @click="activeFilter = 'all'">
            {{ $t('samergo_my_network_show_all') }}
          </button>
        </div>

        <!-- Contact grid -->
        <div v-else class="szw-network__grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="contact in filteredContacts"
            :key="contact.elastic_id"
            class="szw-nc-card"
          >
            <!-- Card header -->
            <div class="szw-nc-card__top tw-flex items-center gap-3">
              <div class="szw-nc-card__avatar">
                <ActorAvatar :actor="contact" :width="56" variant="rounded" prefer="screenshot"/>
              </div>
              <div class="szw-nc-card__info" style="flex: 1; min-width: 0;">
                <h3 class="szw-nc-card__name">{{ contact.name || 'Onbekend' }}</h3>
                <div class="tw-flex items-center gap-1.5 flex-wrap" style="margin-top: 3px;">
                  <span v-if="contact.legend" class="szw-nc-card__legend-badge">
                    {{ taxonomyIdToLabel[contact.legend] || contact.legend }}
                  </span>
                  <span v-if="contact.owner_actor_name" class="szw-nc-card__owner-badge">
                    {{ contact.owner_actor_name }}
                  </span>
                </div>
                <p v-if="contact.description" class="szw-nc-card__desc">{{ contact.description }}</p>
              </div>
              <div class="tw-flex gap-1 flex-shrink-0 items-start">
                <button
                  class="szw-nc-card__action-btn"
                  :title="$t('samergo_my_network_view_profile')"
                  @click="$router.push('/actors/' + contact.elastic_id)"
                >
                  <span class="material-symbols-outlined">open_in_new</span>
                </button>
                <!-- Transfer button (only if org claims exist) -->
                <button
                  v-if="myClaims.length"
                  class="szw-nc-card__action-btn"
                  :title="$t('samergo_my_network_transfer_title')"
                  @click.stop="openTransferPicker(contact, $event)"
                >
                  <span class="material-symbols-outlined">swap_horiz</span>
                </button>
                <template v-if="confirmDeleteId === contact.elastic_id">
                  <button
                    class="szw-nc-card__action-btn szw-nc-card__action-btn--confirm-delete"
                    :title="$t('samergo_my_network_delete_confirm')"
                    @click="removeContact(contact.elastic_id)"
                  >
                    <span class="material-symbols-outlined">check</span>
                  </button>
                  <button
                    class="szw-nc-card__action-btn"
                    :title="$t('samergo_my_network_cancel')"
                    @click="confirmDeleteId = null"
                  >
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </template>
                <button
                  v-else
                  class="szw-nc-card__action-btn szw-nc-card__action-btn--danger"
                  :title="$t('samergo_network_remove_from_wijk')"
                  @click="confirmDeleteId = contact.elastic_id"
                >
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>

            <!-- Phone field -->
            <div class="szw-nc-card__field">
              <span class="material-symbols-outlined szw-nc-card__field-icon">phone</span>
              <template v-if="editingId === contact.elastic_id + '_phone'">
                <input
                  :ref="'phone_' + contact.elastic_id"
                  v-model="editDraft.phone"
                  class="szw-nc-card__field-input"
                  :placeholder="$t('samergo_my_network_phone_input_placeholder')"
                  type="tel"
                  @keyup.enter="saveEdit(contact.elastic_id)"
                  @keyup.esc="cancelEdit"
                />
                <div class="szw-nc-card__edit-actions">
                  <button class="szw-nc-card__confirm-btn" :title="$t('samergo_my_network_save')" @mousedown.prevent="saveEdit(contact.elastic_id)">
                    <span class="material-symbols-outlined">check</span>
                  </button>
                  <button class="szw-nc-card__cancel-btn" title="Annuleren" @mousedown.prevent="cancelEdit">
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              </template>
              <span
                v-else
                class="szw-nc-card__field-value"
                :class="{ 'szw-nc-card__field-value--empty': !contact.phone }"
                @click="startEdit(contact, 'phone')"
              >
                {{ contact.phone || $t('samergo_my_network_phone_placeholder') }}
              </span>
            </div>

            <!-- Notes field -->
            <div class="szw-nc-card__field szw-nc-card__field--notes">
              <span class="material-symbols-outlined szw-nc-card__field-icon">notes</span>
              <template v-if="editingId === contact.elastic_id + '_notes'">
                <textarea
                  :ref="'notes_' + contact.elastic_id"
                  v-model="editDraft.notes"
                  class="szw-nc-card__field-input szw-nc-card__field-input--textarea"
                  :placeholder="$t('samergo_my_network_notes_placeholder')"
                  rows="3"
                  @keyup.esc="cancelEdit"
                />
                <div class="szw-nc-card__edit-actions szw-nc-card__edit-actions--col">
                  <button class="szw-nc-card__confirm-btn" :title="$t('samergo_my_network_save')" @mousedown.prevent="saveEdit(contact.elastic_id)">
                    <span class="material-symbols-outlined">check</span>
                  </button>
                  <button class="szw-nc-card__cancel-btn" title="Annuleren" @mousedown.prevent="cancelEdit">
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              </template>
              <span
                v-else
                class="szw-nc-card__field-value szw-nc-card__field-value--notes"
                :class="{ 'szw-nc-card__field-value--empty': !contact.notes }"
                @click="startEdit(contact, 'notes')"
              >
                {{ contact.notes || $t('samergo_my_network_notes_add') }}
              </span>
            </div>

            <!-- Contributors section -->
            <div class="szw-nc-contributors">
              <button
                class="szw-nc-contributors__toggle"
                @click="toggleContributors(contact.elastic_id)"
              >
                <span class="material-symbols-outlined szw-nc-contributors__toggle-icon">group</span>
                <span>{{ $t('samergo_my_network_contributors') }}</span>
                <span
                  v-if="contributorsMap[contact.elastic_id]?.length"
                  class="szw-nc-contributors__count"
                >{{ contributorsMap[contact.elastic_id].length }}</span>
                <span class="material-symbols-outlined szw-nc-contributors__chevron" :class="{ 'szw-nc-contributors__chevron--open': expandedContributors[contact.elastic_id] }">expand_more</span>
              </button>

              <div v-if="expandedContributors[contact.elastic_id]" class="szw-nc-contributors__list">
                <!-- Loading -->
                <template v-if="contributorsLoading[contact.elastic_id]">
                  <div v-for="n in 2" :key="n" class="szw-nc-contributor szw-nc-contributor--skeleton">
                    <div class="szw-nc-contributor__avatar shimmer-contributor"></div>
                    <div class="szw-nc-contributor__info">
                      <div class="shimmer-contributor" style="height: 12px; width: 60%; border-radius: 4px;"></div>
                      <div class="shimmer-contributor" style="height: 10px; width: 40%; border-radius: 4px; margin-top: 4px;"></div>
                    </div>
                  </div>
                </template>

                <!-- Empty -->
                <p v-else-if="!contributorsMap[contact.elastic_id]?.length" class="szw-nc-contributors__empty">
                  {{ $t('samergo_my_network_no_contributors') }}
                </p>

                <!-- Contributor rows -->
                <template v-else>
                  <div
                    v-for="contributor in contributorsMap[contact.elastic_id]"
                    :key="contributor.user_id"
                    class="szw-nc-contributor"
                  >
                    <!-- Avatar + name -->
                    <div class="szw-nc-contributor__avatar">
                      <img v-if="contributor.photo_url" :src="contributor.photo_url" :alt="contributor.user_name" class="szw-nc-contributor__avatar-img"/>
                      <span v-else class="material-symbols-outlined" style="font-size: 18px; color: #071c5b;">person</span>
                    </div>
                    <div class="szw-nc-contributor__body">
                      <p class="szw-nc-contributor__name">{{ contributor.user_name }}</p>

                      <!-- Contributor phone -->
                      <div class="szw-nc-contributor__field">
                        <span class="material-symbols-outlined szw-nc-contributor__field-icon">phone</span>
                        <template v-if="editingId === contact.elastic_id + '_c_' + contributor.user_id + '_phone'">
                          <input
                            :ref="'cphone_' + contact.elastic_id + '_' + contributor.user_id"
                            v-model="editDraft.phone"
                            class="szw-nc-card__field-input"
                            :placeholder="$t('samergo_my_network_contributor_phone_placeholder')"
                            type="tel"
                            @keyup.enter="saveContributorEdit(contact, contributor.user_id)"
                            @keyup.esc="cancelEdit"
                          />
                          <div class="szw-nc-card__edit-actions">
                            <button class="szw-nc-card__confirm-btn" :title="$t('samergo_my_network_save')" @mousedown.prevent="saveContributorEdit(contact, contributor.user_id)">
                              <span class="material-symbols-outlined">check</span>
                            </button>
                            <button class="szw-nc-card__cancel-btn" title="Annuleren" @mousedown.prevent="cancelEdit">
                              <span class="material-symbols-outlined">close</span>
                            </button>
                          </div>
                        </template>
                        <span
                          v-else
                          class="szw-nc-card__field-value"
                          :class="{ 'szw-nc-card__field-value--empty': !getContributorData(contact, contributor.user_id).phone }"
                          @click="startContributorEdit(contact, contributor.user_id, 'phone')"
                        >
                          {{ getContributorData(contact, contributor.user_id).phone || $t('samergo_my_network_phone_placeholder') }}
                        </span>
                      </div>

                      <!-- Contributor notes -->
                      <div class="szw-nc-contributor__field">
                        <span class="material-symbols-outlined szw-nc-contributor__field-icon">notes</span>
                        <template v-if="editingId === contact.elastic_id + '_c_' + contributor.user_id + '_notes'">
                          <textarea
                            :ref="'cnotes_' + contact.elastic_id + '_' + contributor.user_id"
                            v-model="editDraft.notes"
                            class="szw-nc-card__field-input szw-nc-card__field-input--textarea"
                            :placeholder="$t('samergo_my_network_contributor_notes_placeholder')"
                            rows="2"
                            @keyup.esc="cancelEdit"
                          />
                          <div class="szw-nc-card__edit-actions szw-nc-card__edit-actions--col">
                            <button class="szw-nc-card__confirm-btn" :title="$t('samergo_my_network_save')" @mousedown.prevent="saveContributorEdit(contact, contributor.user_id)">
                              <span class="material-symbols-outlined">check</span>
                            </button>
                            <button class="szw-nc-card__cancel-btn" title="Annuleren" @mousedown.prevent="cancelEdit">
                              <span class="material-symbols-outlined">close</span>
                            </button>
                          </div>
                        </template>
                        <span
                          v-else
                          class="szw-nc-card__field-value szw-nc-card__field-value--notes"
                          :class="{ 'szw-nc-card__field-value--empty': !getContributorData(contact, contributor.user_id).notes }"
                          @click="startContributorEdit(contact, contributor.user_id, 'notes')"
                        >
                          {{ getContributorData(contact, contributor.user_id).notes || $t('samergo_my_network_contributor_notes_add') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>

    <SimplifiedFooterSamergo/>

    <!-- Transfer / copy picker (teleported to escape overflow) -->
    <Teleport to="body">
      <div
        v-if="transferPickerContactId && transferPickerPos && activeTransferContact"
        class="szw-popup"
        :style="{ top: transferPickerPos.top + 'px', right: transferPickerPos.right + 'px' }"
        @click.stop
      >
        <div class="szw-popup__header">
          <span class="material-symbols-outlined szw-popup__header-icon">swap_horiz</span>
          <span class="szw-popup__header-label">{{ $t('samergo_my_network_transfer_title') }}</span>
        </div>
        <div class="szw-popup__options">
          <template v-if="myClaims.filter(c => c.actor_id !== activeTransferContact.owner_actor_id).length">
            <p class="szw-popup__section-label">{{ $t('samergo_transfer_move_to') }}</p>
            <template v-for="claim in myClaims" :key="'move-' + claim.actor_id">
              <button
                v-if="claim.actor_id !== activeTransferContact.owner_actor_id"
                class="szw-popup__option"
                @click="transferContact(activeTransferContact, claim.actor_id, false)"
              >
                <span class="szw-popup__avatar">
                  <img v-if="claim.logo" :src="claim.logo" class="szw-popup__avatar-img"/>
                  <span v-else class="material-symbols-outlined">business</span>
                </span>
                <span class="szw-popup__option-name">{{ claim.name }}</span>
              </button>
            </template>
            <hr class="szw-popup__divider"/>
            <p class="szw-popup__section-label">{{ $t('samergo_transfer_copy_to') }}</p>
            <template v-for="claim in myClaims" :key="'copy-' + claim.actor_id">
              <button
                v-if="claim.actor_id !== activeTransferContact.owner_actor_id"
                class="szw-popup__option"
                @click="transferContact(activeTransferContact, claim.actor_id, true)"
              >
                <span class="szw-popup__avatar">
                  <img v-if="claim.logo" :src="claim.logo" class="szw-popup__avatar-img"/>
                  <span v-else class="material-symbols-outlined">business</span>
                </span>
                <span class="szw-popup__option-name">{{ claim.name }}</span>
                <span class="material-symbols-outlined szw-popup__copy-icon">content_copy</span>
              </button>
            </template>
          </template>
          <p v-else class="szw-popup__empty">{{ $t('samergo_transfer_no_options') }}</p>
        </div>
      </div>
    </Teleport>

    <!-- FAB: discover providers -->
    <button class="szw-fab" @click="$router.push('/actors-simplified')">
      <span class="material-symbols-outlined" style="font-size: 24px;">person_search</span>
    </button>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterSamergo from '@/components/SimplifiedFooter/SimplifiedFooterSamergo.vue'
  import { ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import { fetchContributors } from '@/api/actors'
  import { fetchMyClaims } from '@/api/network-contacts'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'

  export default defineComponent({
    name: 'StaticHomeSamergoNetwork',
    components: { ActorAvatar, SimplifiedFooterSamergo },
    mixins: [TaxonomyMixin],
    data() {
      return {
        searchQuery: '',
        editingId: null as string | null,
        editDraft: { phone: '', notes: '' },
        confirmDeleteId: null as string | null,
        transferPickerContactId: null as string | null,
        transferPickerPos: null as { top: number; right: number } | null,
        contributorsMap: {} as Record<string, any[]>,
        contributorsLoading: {} as Record<string, boolean>,
        expandedContributors: {} as Record<string, boolean>,
        myClaims: [] as Array<{ actor_id: number; elastic_id: string; name: string; logo: string | null }>,
        activeFilter: 'all' as string | number,
      }
    },
    async mounted() {
      await this.$store.dispatch(ACTION_TYPES.FETCH)
      this.contacts.forEach((c: any) => this.loadContributors(c.elastic_id))
      this.loadMyClaims()
      document.addEventListener('click', this.closePopups)
    },
    beforeUnmount() {
      document.removeEventListener('click', this.closePopups)
    },
    computed: {
      profile(): any {
        return this.$store.state.user.profile || {}
      },
      contacts() {
        return this.$store.getters.networkContacts
      },
      activeTransferContact(): any {
        if (!this.transferPickerContactId) return null
        return (this.contacts as any[]).find((c: any) => c.elastic_id === this.transferPickerContactId) ?? null
      },
      filteredContacts() {
        let list = this.contacts as any[]

        if (this.activeFilter === 'personal') {
          list = list.filter((c: any) => !c.owner_actor_id)
        } else if (this.activeFilter !== 'all') {
          list = list.filter((c: any) => c.owner_actor_id === this.activeFilter)
        } else {
          // Deduplicate by elastic_id on "Alles" tab
          const seen = new Set<string>()
          list = list.filter((c: any) => {
            if (seen.has(c.elastic_id)) return false
            seen.add(c.elastic_id)
            return true
          })
        }

        if (this.searchQuery.trim()) {
          const q = this.searchQuery.toLowerCase()
          list = list.filter((c: any) =>
            (c.name && c.name.toLowerCase().includes(q)) ||
            (c.notes && c.notes.toLowerCase().includes(q))
          )
        }

        return list
      },
      taxonomyIdToLabel(): Record<string, string> {
        const maps = [
          this.$store.getters.categoryValuesAndLabels,
          this.$store.getters.activityValuesAndLabels,
          this.$store.getters.domainValuesAndLabels,
          this.$store.getters.technologyValuesAndLabels,
          this.$store.getters.membershipValuesAndLabels,
        ]
        const out: Record<string, string> = {}
        maps.forEach((m: Array<{ value: string; label: string }>) => {
          if (Array.isArray(m)) m.forEach(({ value, label }) => {
            out[value] = label
          })
        })
        return out
      },
    },
    methods: {
      openProfileModal() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
      },
      closePopups() {
        this.transferPickerContactId = null
        this.transferPickerPos = null
      },
      openTransferPicker(contact: any, event: MouseEvent) {
        if (this.transferPickerContactId === contact.elastic_id) {
          this.closePopups()
          return
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        this.transferPickerPos = { top: rect.bottom + 8, right: window.innerWidth - rect.right }
        this.transferPickerContactId = contact.elastic_id
      },
      async loadMyClaims() {
        try {
          this.myClaims = await fetchMyClaims()
        } catch {
          // non-critical
        }
      },
      async transferContact(contact: any, ownerActorId: number | null, duplicate: boolean) {
        this.transferPickerContactId = null
        await this.$store.dispatch(ACTION_TYPES.TRANSFER_CONTACT, {
          elasticId: contact.elastic_id,
          ownerActorId,
          duplicate,
        })
      },
      async loadContributors(elasticId: string) {
        if (this.contributorsMap[elasticId] !== undefined) return
        this.contributorsLoading = { ...this.contributorsLoading, [elasticId]: true }
        try {
          const res = await fetchContributors(elasticId, { includeOwners: true })
          this.contributorsMap = { ...this.contributorsMap, [elasticId]: Array.isArray(res) ? res : [] }
        } catch {
          this.contributorsMap = { ...this.contributorsMap, [elasticId]: [] }
        } finally {
          this.contributorsLoading = { ...this.contributorsLoading, [elasticId]: false }
        }
      },
      toggleContributors(elasticId: string) {
        if (!this.expandedContributors[elasticId] && this.contributorsMap[elasticId] === undefined) {
          this.loadContributors(elasticId)
        }
        this.expandedContributors = { ...this.expandedContributors, [elasticId]: !this.expandedContributors[elasticId] }
      },
      getContributorData(contact: any, userId: number): { phone: string; notes: string } {
        return (contact.contributors_data ?? {})[userId] ?? { phone: '', notes: '' }
      },
      startContributorEdit(contact: any, userId: number, field: 'phone' | 'notes') {
        const data = this.getContributorData(contact, userId)
        this.editingId = `${contact.elastic_id}_c_${userId}_${field}`
        this.editDraft = { phone: data.phone || '', notes: data.notes || '' }
        this.$nextTick(() => {
          const refKey = (field === 'phone' ? 'cphone_' : 'cnotes_') + contact.elastic_id + '_' + userId
          const el = (this.$refs as any)[refKey]
          if (el) (Array.isArray(el) ? el[0] : el).focus()
        })
      },
      saveContributorEdit(contact: any, userId: number) {
        if (!this.editingId) return
        const existing = contact.contributors_data ?? {}
        const updated = {
          ...existing,
          [userId]: { phone: this.editDraft.phone, notes: this.editDraft.notes },
        }
        this.$store.dispatch(ACTION_TYPES.UPDATE_CONTACT, {
          elasticId: contact.elastic_id,
          phone: contact.phone,
          notes: contact.notes,
          contributors_data: updated,
        })
        this.editingId = null
      },
      removeContact(elasticId: string) {
        this.$store.dispatch(ACTION_TYPES.REMOVE_CONTACT, elasticId)
        this.confirmDeleteId = null
      },
      startEdit(contact: any, field: 'phone' | 'notes') {
        this.editingId = contact.elastic_id + '_' + field
        this.editDraft = { phone: contact.phone || '', notes: contact.notes || '' }
        this.$nextTick(() => {
          const refKey = field + '_' + contact.elastic_id
          const el = (this.$refs as any)[refKey]
          if (el) (Array.isArray(el) ? el[0] : el).focus()
        })
      },
      saveEdit(elasticId: string) {
        if (!this.editingId) return
        this.$store.dispatch(ACTION_TYPES.UPDATE_CONTACT, {
          elasticId,
          phone: this.editDraft.phone,
          notes: this.editDraft.notes,
        })
        this.editingId = null
      },
      cancelEdit() {
        this.editingId = null
      },
    },
  })
</script>

<style scoped lang="scss">
  $szw-navy: #071c5b;
  $szw-navy-dark: #00082f;
  $szw-green: #006e29;
  $szw-surface: #f8f9fa;
  $szw-surface-low: #f3f4f5;
  $szw-border: #c6c5d2;
  $szw-text: #191c1d;
  $szw-text-muted: #454650;

  .szw-network {
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    background: $szw-surface;
    min-height: 100vh;
    color: $szw-text;
  }

  .szw-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  // ── Hero ──────────────────────────────────────────────────────────────────

  .szw-network-hero {
    position: relative;
    background: $szw-navy;
    padding: 64px 0 80px;
    overflow: hidden;
  }

  .szw-network-hero__bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .szw-network-hero__bg-shape {
    position: absolute;

    &--right {
      top: 0;
      right: 0;
      width: 60%;
      height: 100%;
    }
  }

  .szw-network-hero__blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);

    &--teal {
      top: -40px;
      right: 10%;
      width: 300px;
      height: 300px;
      background: #006e29;
      opacity: 0.25;
    }

    &--gold {
      bottom: -60px;
      left: 5%;
      width: 240px;
      height: 240px;
      background: #f5c343;
      opacity: 0.15;
    }
  }

  .szw-network-hero__content {
    position: relative;
    z-index: 10;
  }

  .szw-network-hero__icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: #7df98f;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .szw-network-hero__title {
    font-size: clamp(1.5rem, 3vw, 2.25rem);
    font-weight: 700;
    color: white;
    line-height: 1.2;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .szw-network-hero__subtitle {
    font-size: 0.95rem;
    color: rgba(219, 234, 254, 0.7);
    margin-top: 4px;
  }

  .szw-network-hero__edit-location-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    color: rgba(219, 234, 254, 0.7);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }

  .szw-network-hero__count {
    margin-left: auto;
    background: rgba(255, 255, 255, 0.12);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 6px 18px;
    border-radius: 9999px;
    white-space: nowrap;
  }

  // ── Filter tabs ───────────────────────────────────────────────────────────

  .szw-network-hero__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 24px;
  }

  .szw-network-hero__tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 16px;
    border-radius: 9999px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.82rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
      color: white;
    }

    &--active {
      background: white;
      color: $szw-navy;
      border-color: white;
    }
  }

  .szw-network-hero__search-wrapper {
    position: relative;
    margin-top: 32px;
    max-width: 640px;
  }

  .szw-network-hero__search-glow {
    position: absolute;
    inset: -4px;
    background: linear-gradient(to right, #7df98f, #dde1ff);
    border-radius: 16px;
    filter: blur(8px);
    opacity: 0.25;
  }

  .szw-network-hero__search-box {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 14px;
    padding: 6px 6px 6px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  .szw-network-hero__search-icon {
    padding: 0 12px 0 16px;
    color: $szw-navy;
    flex-shrink: 0;
  }

  .szw-network-hero__search-input {
    flex: 1;
    padding: 10px 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 0.95rem;
    font-family: inherit;
    color: $szw-text;

    &::placeholder {
      color: #757681;
    }
  }

  .szw-network-hero__search-clear {
    background: $szw-surface-low;
    border: none;
    color: $szw-text-muted;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 6px;
    border-radius: 8px;
    margin-right: 4px;
    transition: background 0.15s;

    &:hover {
      background: $szw-border;
    }
  }

  // ── Section ───────────────────────────────────────────────────────────────

  .szw-section {
    padding: 64px 0;
    min-height: calc(100vh - 75px - 52px - 303px);
  }

  // ── Empty state ───────────────────────────────────────────────────────────

  .szw-network__empty {
    text-align: center;
    padding: 80px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .szw-network__empty-icon-wrap {
    width: 96px;
    height: 96px;
    background: #dde1ff;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .szw-network__empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
  }

  .szw-network__empty-body {
    font-size: 0.95rem;
    color: $szw-text-muted;
    max-width: 400px;
    line-height: 1.6;
    margin: 0;
  }

  .szw-network__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    margin-top: 8px;
    transition: opacity 0.15s, transform 0.1s;

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
    }

    &--primary {
      background: $szw-navy;
      color: white;
    }

    &--ghost {
      background: $szw-surface-low;
      color: $szw-text-muted;
      border: 1px solid $szw-border;
    }
  }

  // ── Contact grid ──────────────────────────────────────────────────────────

  .szw-network__grid {
    // spacing via grid gap
  }

  // ── Contact card ──────────────────────────────────────────────────────────

  .szw-nc-card {
    background: white;
    border: 1px solid $szw-border;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s, transform 0.15s;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09);
      transform: translateY(-1px);
    }
  }

  .szw-nc-card__top {
    // layout via tw-flex
  }

  .szw-nc-card__avatar {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .szw-nc-card__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-nc-card__name {
    font-size: 0.95rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-nc-card__role {
    font-size: 0.78rem;
    color: $szw-text-muted;
    margin-top: 2px;
  }

  .szw-nc-card__action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $szw-text-muted;
    transition: background 0.15s, color 0.15s;

    .material-symbols-outlined {
      font-size: 18px;
    }

    &:hover {
      background: $szw-surface-low;
      color: $szw-text;
    }

    &--danger:hover {
      background: #ffdad6;
      color: #ba1a1a;
    }

    &--confirm-delete {
      background: #ba1a1a;
      color: white;

      &:hover {
        background: #93000a;
      }
    }
  }

  // ── Editable fields ───────────────────────────────────────────────────────

  .szw-nc-card__field {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-top: 1px solid $szw-border;
    padding-top: 12px;
  }

  .szw-nc-card__field-icon {
    font-size: 16px;
    color: $szw-text-muted;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .szw-nc-card__field-value {
    font-size: 0.875rem;
    color: $szw-text;
    line-height: 1.5;
    cursor: text;
    flex: 1;
    border-radius: 6px;
    padding: 2px 6px;
    margin: -2px -6px;
    transition: background 0.15s;

    &:hover {
      background: $szw-surface-low;
    }

    &--empty {
      color: #94a3b8;
      font-style: italic;
    }

    &--notes {
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  .szw-nc-card__field-input {
    flex: 1;
    font-size: 0.875rem;
    font-family: inherit;
    color: $szw-text;
    background: $szw-surface-low;
    border: 1.5px solid $szw-navy;
    border-radius: 8px;
    padding: 6px 10px;
    outline: none;
    line-height: 1.5;
    transition: border-color 0.15s;

    &:focus {
      border-color: #4b60b8;
    }

    &--textarea {
      resize: none;
      width: 100%;
    }
  }

  .szw-nc-card__edit-actions {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-shrink: 0;
    align-self: flex-start;

    &--col {
      flex-direction: column;
    }
  }

  .szw-nc-card__confirm-btn,
  .szw-nc-card__cancel-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    .material-symbols-outlined {
      font-size: 16px;
    }
  }

  .szw-nc-card__confirm-btn {
    background: $szw-navy;
    color: white;

    &:hover {
      background: #4b60b8;
    }
  }

  .szw-nc-card__cancel-btn {
    background: $szw-surface-low;
    color: $szw-text-muted;
    border: 1px solid $szw-border;

    &:hover {
      background: #ffdad6;
      color: #ba1a1a;
      border-color: #ffdad6;
    }
  }

  // ── Contributors section ──────────────────────────────────────────────────

  .szw-nc-contributors {
    border-top: 1px solid $szw-border;
    padding-top: 10px;
  }

  .szw-nc-contributors__toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 6px;
    margin: -4px -6px;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: $szw-text-muted;
    font-family: inherit;
    width: 100%;
    text-align: left;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: $szw-surface-low;
      color: $szw-text;
    }

    .material-symbols-outlined {
      font-size: 16px;
    }
  }

  .szw-nc-contributors__toggle-icon {
    color: $szw-navy;
    opacity: 0.6;
  }

  .szw-nc-contributors__count {
    background: $szw-surface-low;
    border-radius: 9999px;
    padding: 1px 7px;
    font-size: 0.75rem;
    color: $szw-text-muted;
  }

  .szw-nc-contributors__chevron {
    margin-left: auto;
    transition: transform 0.2s;

    &--open {
      transform: rotate(180deg);
    }
  }

  .szw-nc-contributors__list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .szw-nc-contributors__empty {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    margin: 0;
    padding: 4px 0;
  }

  .szw-nc-contributor {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 10px;
    background: $szw-surface-low;
    border-radius: 10px;
    border: 1px solid $szw-border;

    &--skeleton {
      pointer-events: none;
    }
  }

  .szw-nc-contributor__avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .szw-nc-contributor__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-nc-contributor__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .szw-nc-contributor__name {
    font-size: 0.82rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
    line-height: 1.3;
  }

  .szw-nc-contributor__function {
    font-size: 0.75rem;
    color: $szw-text-muted;
    margin: 0;
  }

  .szw-nc-contributor__field {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  .szw-nc-contributor__field-icon {
    font-size: 14px;
    color: $szw-text-muted;
    margin-top: 2px;
    flex-shrink: 0;
  }

  @keyframes shimmer-anim {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .shimmer-contributor {
    background: linear-gradient(90deg, #e0e2e8 25%, #edeef2 50%, #e0e2e8 75%);
    background-size: 800px 100%;
    animation: shimmer-anim 1.4s infinite linear;
  }

  // ── Card badges & description ─────────────────────────────────────────────

  .szw-nc-card__legend-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #f3f4f5;
    color: $szw-text-muted;
    border: 1px solid $szw-border;
    white-space: nowrap;
  }

  .szw-nc-card__owner-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #dde1ff;
    color: $szw-navy;
    white-space: nowrap;
  }

  .szw-nc-card__desc {
    font-size: 0.78rem;
    color: $szw-text-muted;
    margin: 4px 0 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // ── FAB ───────────────────────────────────────────────────────────────────

  .szw-fab {
    position: fixed;
    bottom: 32px;
    right: 32px;
    width: 56px;
    height: 56px;
    background: $szw-navy;
    color: white;
    border: none;
    border-radius: 50%;
    box-shadow: 0 8px 24px rgba(7, 28, 91, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    z-index: 100;

    &:hover {
      transform: scale(1.08);
      box-shadow: 0 12px 32px rgba(7, 28, 91, 0.45);
    }

    &:active {
      transform: scale(0.95);
    }
  }
</style>

<style lang="scss">
  // ── Transfer/copy popup (unscoped — rendered via Teleport outside component root) ──

  .szw-popup {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid #e2e4ea;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(7, 28, 91, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
    min-width: 220px;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', Inter, sans-serif;
  }

  .szw-popup__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid #f0f1f5;
    background: #f8f9ff;
  }

  .szw-popup__header-icon {
    font-size: 16px;
    color: #071c5b;
    opacity: 0.7;
  }

  .szw-popup__header-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #071c5b;
    letter-spacing: -0.01em;
  }

  .szw-popup__options {
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .szw-popup__section-label {
    font-size: 0.68rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 6px 10px 4px;
    margin: 0;
  }

  .szw-popup__divider {
    border: none;
    border-top: 1px solid #f0f1f5;
    margin: 4px 0;
  }

  .szw-popup__option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.12s;

    &:hover {
      background: #f3f4f8;
    }

    &:active {
      background: #e8eaf2;
    }
  }

  .szw-popup__avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #071c5b;

    .material-symbols-outlined {
      font-size: 17px;
    }
  }

  .szw-popup__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-popup__option-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #191c1d;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .szw-popup__copy-icon {
    font-size: 14px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  .szw-popup__empty {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    text-align: center;
    padding: 12px 16px;
    margin: 0;
  }
</style>
